import React, { useState } from 'react';
import { 
  Drawer, Box, Typography, IconButton, Divider,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  TextField, Button, Chip, Paper, Collapse, InputAdornment,
  LinearProgress, Alert, useMediaQuery, useTheme, Fade, Zoom, Grow
} from '@mui/material';
import { 
  Close, Delete, LocalOffer, ShoppingCart, NoteAdd, Close as CloseIcon,
  Diamond as DiamondIcon, AttachMoney as AttachMoneyIcon,
  Warning as WarningIcon, CheckCircle as CheckCircleIcon,
  Remove as RemoveIcon, Add as AddIcon
} from '@mui/icons-material';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import noPhoto from "../assets/no_photo.webp";
import { useLanguage } from '../context/LanguageContext';
import keywordsData from '../lang/keywords.json';
import currencyTrans, { formatPrice, getCurrencySymbol, getCurrencyShort, getCurrencyFullName } from './CarrancyTrans';

const CartDrawer = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [openNoteInputs, setOpenNoteInputs] = useState({});
  const [itemNotes, setItemNotes] = useState({});
  
  const {
    cart,
    cartOpen,
    setCartOpen,
    updateQuantity,
    removeFromCart,
    cartTotal,
    totalSavings,
    loading,
    getItemPrice,
    getOriginalPrice,
    hasDiscount,
    deliverFees,
    serviceChargePercent,
    serviceChargeValue,
    subtotalAfterThreshold,
    thresholdDiscount,
    subtotalBeforeThreshold,
    updateItemNotes,
    isVipTable,
    minCharge,
    tableInfo,
    getRemainingMinCharge,
    getMinChargeProgress,
    minChargeError,
    minChargeMessage,
    isOrder, cartCurrency
  } = useCart();

  const goCheckoutTxt = language === 'ar' ? keywordsData.check_out_txt_ar : keywordsData.check_out_txt_en;
  const goCheckoutWithDiscountTxt = language === 'ar' ? keywordsData.check_out_dicount_txt_ar : keywordsData.check_out_dicount_txt_en;

  // Check if checkout is allowed
  const isCheckoutAllowed = () => {
    if (isVipTable && !isOrder && minCharge > 0) {
      return cartTotal >= minCharge;
    }
    return true;
  };

  // Toggle note input for a specific item
  const toggleNoteInput = (itemId) => {
    setOpenNoteInputs(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  // Handle note change
  const handleNoteChange = (itemId, note) => {
    setItemNotes(prev => ({
      ...prev,
      [itemId]: note
    }));
    
    if (updateItemNotes) {
      updateItemNotes(itemId, note);
    }
  };

  const handleCheckout = () => {
    if (!isCheckoutAllowed()) {
      return;
    }
    setCartOpen(false);
    navigate('/checkout');
  };

  // Responsive drawer width
  const drawerWidth = isMobile ? '100%' : isTablet ? 420 : 450;

  return (
    <Drawer
      anchor="right"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      open={cartOpen}
      onClose={() => setCartOpen(false)}
      PaperProps={{ 
        sx: { 
          width: drawerWidth, 
          maxWidth: '100vw', 
          p: isMobile ? 1.5 : 2,
          backgroundColor: '#000000',
          color: '#FFFFFF',
          borderLeft: '2px solid #FFD700',
        } 
      }}
      sx={{zIndex: 1201}}
    >
      {/* Header */}
      <Fade in timeout={300}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <ShoppingCart sx={{ color: '#FFD700', fontSize: isMobile ? 24 : 28 }} />
            <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ color: '#FFFFFF', fontWeight: 700 }}>
              {language === 'ar' ? keywordsData.cart_txt_title_ar : keywordsData.cart_txt_title_en}
            </Typography>
            {cart.length > 0 && (
              <Chip
                label={cart.reduce((sum, item) => sum + item.quantity, 0)}
                size="small"
                sx={{
                  backgroundColor: '#FFD700',
                  color: '#000000',
                  fontWeight: 'bold',
                  minWidth: 24,
                  height: 24,
                  fontSize: '0.75rem'
                }}
              />
            )}
          </Box>
          <IconButton 
            onClick={() => setCartOpen(false)}
            sx={{
              color: '#B3B3B3',
              '&:hover': {
                color: '#FFD700',
                backgroundColor: 'rgba(255, 215, 0, 0.08)',
              }
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </Fade>

      <Divider sx={{ 
        backgroundColor: '#2D2D2D',
        height: '1px',
        mb: 2
      }} />

      {loading ? (
        <Box p={3} textAlign="center">
          <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? keywordsData.loading_cart_txt_ar : keywordsData.loading_cart_txt_en}</Typography>
        </Box>
      ) : cart.length === 0 ? (
        <Zoom in>
          <Box p={3} textAlign="center">
            <ShoppingCart sx={{ color: '#2D2D2D', fontSize: 64, mb: 2 }} />
            <Typography sx={{ color: '#B3B3B3', mb: 2 }}>{language === 'ar' ? keywordsData.empty_cart_txt_ar : keywordsData.empty_cart_txt_en}</Typography>
            <Button
              variant="outlined"
              onClick={() => setCartOpen(false)}
              sx={{
                color: '#FFD700',
                borderColor: '#FFD700',
                borderRadius: '50px',
                px: 3,
                '&:hover': {
                  borderColor: '#FFE55C',
                  backgroundColor: 'rgba(255, 215, 0, 0.08)',
                }
              }}
            >
              {language === 'ar' ? keywordsData.continue_shoping_txt_ar : keywordsData.continue_shoping_txt_en}
            </Button>
          </Box>
        </Zoom>
      ) : (
        <>
          {/* Items List */}
          <List sx={{ 
            flex: 1, 
            overflow: 'auto', 
            minHeight: 300,
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#2D2D2D',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#FFD700',
              borderRadius: '4px',
            },
          }}>
            {cart.map((item, index) => {
              const currentPrice = getItemPrice(item);
              const originalPrice = getOriginalPrice(item);
              const isDiscounted = hasDiscount(item);
              const discountPercent = isDiscounted 
                ? Math.round((1 - currentPrice / originalPrice) * 100)
                : 0;
              
              const itemNote = itemNotes[item.id] || item.notes || '';
              const isNoteOpen = openNoteInputs[item.id] || false;

              return (
                <Grow in timeout={300 + index * 50}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#0D0D0D',
                      border: '1px solid #2D2D2D',
                      borderRadius: '16px',
                      mb: 1.5,
                      p: isMobile ? 1.5 : 2,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        border: '1px solid #FFD700',
                        backgroundColor: '#121212',
                        transform: 'translateX(-4px)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: isMobile ? 1.5 : 2 }}>
                      {/* Product Image */}
                      <Box sx={{ 
                        width: isMobile ? 60 : 70, 
                        height: isMobile ? 60 : 70, 
                        position: 'relative',
                        backgroundColor: '#1A1A1A',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <img
                          src={item.product?.imageUrl || noPhoto}
                          alt={item.product?.name}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            borderRadius: '12px'
                          }}
                        />
                        {isDiscounted && (
                          <Chip
                            label={`-${discountPercent}%`}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              backgroundColor: '#FFD700',
                              color: '#000000',
                              fontWeight: 'bold',
                              fontSize: '0.6rem',
                              height: 20,
                              minWidth: 40,
                              border: '2px solid #000000',
                            }}
                          />
                        )}
                      </Box>
                      
                      {/* Product Details */}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography 
                            variant={isMobile ? "body2" : "subtitle2"} 
                            sx={{ 
                              color: '#FFFFFF',
                              fontWeight: 700,
                              fontSize: isMobile ? '0.85rem' : '1rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: isMobile ? 120 : 'auto'
                            }}
                          >
                            {language === 'ar' ? item.product?.name : item.product?.name_en}
                          </Typography>
                          
                          {/* Note Button */}
                          <IconButton
                            size="small"
                            onClick={() => toggleNoteInput(item.id)}
                            sx={{ 
                              color: itemNote ? '#FFD700' : '#B3B3B3',
                              p: 0.5,
                              ml: 1
                            }}
                          >
                            <NoteAdd fontSize="small" /> 
                          </IconButton>
                        </Box>
                        
                        {/* Price Display */}
                        <Box sx={{ mb: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#FFD700',
                              fontWeight: 700,
                              fontSize: isMobile ? '0.9rem' : '1rem',
                              display: 'inline-block',
                              mr: 1
                            }}
                          >
                            {formatPrice(currentPrice.toFixed(2),cartCurrency,language)}
                          </Typography>
                          {isDiscounted && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                textDecoration: 'line-through', 
                                color: '#6B6B6B',
                                fontSize: '0.7rem',
                                display: 'block'
                              }}
                            >
                              {formatPrice(originalPrice.toFixed(2),cartCurrency,language)}
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Quantity Controls */}
                        <Paper
                          elevation={0}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#0D0D0D',
                            border: '1px solid #2D2D2D',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            height: isMobile ? 28 : 32,
                            '&:hover': {
                              borderColor: '#FFD700',
                            }
                          }}
                        >
                          <IconButton 
                            size="small"
                            onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1), item.product.product_limit, item.product?.category?.card_limit)}
                            sx={{
                              minWidth: isMobile ? 28 : 32,
                              height: '100%',
                              backgroundColor: 'transparent',
                              color: '#FFFFFF',
                              borderRadius: 0,
                              padding: 0,
                              '&:hover': {
                                backgroundColor: '#FFD700',
                                color: '#000000',
                              }
                            }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          
                          <Typography
                            sx={{
                              minWidth: isMobile ? 40 : 45,
                              textAlign: 'center',
                              color: '#FFFFFF',
                              fontSize: isMobile ? '0.85rem' : '0.875rem',
                              fontWeight: '500',
                            }}
                          >
                            {item.quantity}
                          </Typography>
                          
                          <IconButton 
                            size="small"
                            onClick={() => updateQuantity(item.id, item.quantity + 1, item.product.product_limit, item.product?.category?.card_limit)}
                            sx={{
                              minWidth: isMobile ? 28 : 32,
                              height: '100%',
                              backgroundColor: 'transparent',
                              color: '#FFFFFF',
                              borderRadius: 0,
                              padding: 0,
                              '&:hover': {
                                backgroundColor: '#FFD700',
                                color: '#000000',
                              }
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      </Box>     
                      
                      {/* Total Price & Delete */}
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        flexShrink: 0
                      }}>
                        <Typography 
                          variant={isMobile ? "body2" : "subtitle1"} 
                          sx={{ 
                            color: '#FFD700',
                            fontWeight: 800,
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {formatPrice((currentPrice * item.quantity).toFixed(2),cartCurrency,language)}
                        </Typography>
                        
                        <IconButton
                          size="small"
                          onClick={() => removeFromCart(item.id)}
                          sx={{
                            color: '#6B6B6B',
                            '&:hover': {
                              color: '#FF3D00',
                              backgroundColor: 'rgba(255, 61, 0, 0.08)',
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    {/* Notes Input - Collapsible */}
                    <Collapse in={isNoteOpen} timeout="auto" unmountOnExit>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        helperText={language === 'ar' ? 'إنجليزي فقط' : 'English only'}
                        placeholder={language === "ar" ? "ملاحظات إضافية (مثل: بدون بصل، إضافي جبن...)" : "Additional notes (e.g., no onion, extra cheese...)"}
                        value={itemNote}
                        onChange={(e) => handleNoteChange(item.id, e.target.value)}
                        InputProps={{
                          endAdornment: itemNote && (
                            <InputAdornment position="end">
                              <IconButton 
                                size="small" 
                                onClick={() => handleNoteChange(item.id, '')}
                                edge="end"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                          sx: {
                            backgroundColor: '#1A1A1A',
                            color: '#FFFFFF',
                            borderRadius: '10px',
                            fontSize: '0.8rem',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#2D2D2D',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#FFD700',
                            },
                          }
                        }}
                      />
                    </Collapse>
                  </Paper>
                </Grow>
              );
            })}
          </List>

          <Divider sx={{ 
            backgroundColor: '#2D2D2D',
            height: '1px',
            my: 2
          }} />

          {/* VIP Table Minimum Charge Section */}
          {isVipTable && minCharge > 0 && (
            <Fade in>
              <Box sx={{ mb: 3 }}>
                <Alert
                  severity={cartTotal >= minCharge ? "success" : "warning"}
                  icon={cartTotal >= minCharge ? <CheckCircleIcon /> : <DiamondIcon />}
                  sx={{
                    mb: 2,
                    backgroundColor: cartTotal >= minCharge 
                      ? 'rgba(76, 175, 80, 0.1)'
                      : 'rgba(156, 39, 176, 0.1)',
                    color: cartTotal >= minCharge ? '#4CAF50' : '#CE93D8',
                    border: `1px solid ${cartTotal >= minCharge ? '#4CAF50' : '#9C27B0'}`,
                    borderRadius: '12px',
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {language === 'ar' 
                      ? `طاولة VIP - الحد الأدنى للطلب: ${formatPrice(minCharge,cartCurrency,language)}`
                      : `VIP Table - Minimum order: ${formatPrice(minCharge,cartCurrency,language)}`
                    }
                  </Typography>
                </Alert>

                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#B3B3B3', fontSize: '0.7rem' }}>
                      {language === 'ar' ? 'تقدم الحد الأدنى' : 'Minimum Progress'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#FFD700', fontWeight: 600, fontSize: '0.7rem' }}>
                      {Math.min(getMinChargeProgress(), 100).toFixed(0)}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(getMinChargeProgress(), 100)} 
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: '#2D2D2D',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getMinChargeProgress() >= 100 ? '#4CAF50' : '#9C27B0',
                        borderRadius: 3,
                      }
                    }}
                  />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: getRemainingMinCharge() > 0 ? '#FF9800' : '#4CAF50',
                      display: 'block',
                      mt: 0.5,
                      fontWeight: 500,
                      fontSize: '0.7rem'
                    }}
                  >
                    {getRemainingMinCharge() > 0 
                      ? `${language === 'ar' ? 'المتبقي' : 'Remaining'}: ${formatPrice(getRemainingMinCharge().toFixed(2) ,cartCurrency,language)}`
                      : language === 'ar' ? '✓ تم تحقيق الحد الأدنى' : '✓ Minimum met'
                    }
                  </Typography>
                </Box>

                {minChargeError && (
                  <Alert 
                    severity="warning" 
                    icon={<WarningIcon />}
                    sx={{ 
                      mb: 2,
                      backgroundColor: 'rgba(255, 152, 0, 0.1)',
                      color: '#FF9800',
                      border: '1px solid #FF9800',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography variant="caption">
                      {minChargeMessage}
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Fade>
          )}

          {/* Summary Section */}
          <Box sx={{ p: isMobile ? 1 : 2 }}>
            {/* Total Savings */}
            {totalSavings > 0 && (
              <Zoom in>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: '#0D0D0D',
                    border: '1px solid #FFD700',
                    borderRadius: '12px',
                    p: 1.5,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <LocalOffer sx={{ color: '#FFD700', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#FFD700', fontWeight: 600 }}>
                      {language === 'ar' ? keywordsData.you_discounted_txt_ar : keywordsData.you_discounted_txt_en}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 800, fontSize: '1rem' }}>
                      {formatPrice(totalSavings.toFixed(2),cartCurrency,language)}
                    </Typography>
                  </Box>
                </Paper>
              </Zoom>
            )}

            {/* Threshold Discount Section */}
            {thresholdDiscount.thresholdvalue > 0 && (
              <Fade in>
                <Box sx={{ 
                  mb: 2,
                  p: 2,
                  backgroundColor: 'rgba(255, 215, 0, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid #2D2D2D',
                  textAlign: 'center'
                }}>
                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 700, fontSize: '0.8rem' }}>
                    {language === 'ar' 
                      ? `وفر ${formatPrice(thresholdDiscount.thresholdvalue ,cartCurrency,language)} عند شراء بـ ${formatPrice(thresholdDiscount.min_amount ,cartCurrency,language)}` 
                      : `Save ${formatPrice(thresholdDiscount.thresholdvalue ,cartCurrency,language)} when you spend ${formatPrice(thresholdDiscount.min_amount ,cartCurrency,language)}`}
                  </Typography>
                </Box>
              </Fade>
            )}

            {/* Delivery Fees */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1
            }}>
              <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '0.8rem' }}>
                {language === 'ar' ? keywordsData.deliver_fees_txt_ar : keywordsData.deliver_fees_txt_en} :
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.8rem' }}>
                {formatPrice(deliverFees.toFixed(2),cartCurrency,language)}
              </Typography>
            </Box>

            {/* Service charge box */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1
            }}>
              <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '0.8rem' }}>
                {language === 'ar' ? 'رسوم الخدمة ' : 'Service charge '} {serviceChargePercent.toFixed(2)}% :
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.8rem' }}>
                {formatPrice(serviceChargeValue.toFixed(2),cartCurrency,language)}
              </Typography>
            </Box>

            {/* Subtotal with threshold discount */}
            {subtotalBeforeThreshold > 0 && thresholdDiscount.applicable && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '0.8rem' }}>
                    {language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.8rem' }}>
                      {formatPrice(subtotalBeforeThreshold.toFixed(2),cartCurrency,language)}
                  </Typography>
                </Box>
                
                {thresholdDiscount.amount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#4CAF50', fontSize: '0.8rem' }}>
                      {language === 'ar' ? 'خصم الكمية' : 'Bulk Discount'}:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 600, fontSize: '0.8rem' }}>
                      -{formatPrice(thresholdDiscount.amount.toFixed(2),cartCurrency,language)}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            <Divider sx={{ my: 1.5, backgroundColor: '#2D2D2D' }} />

            {/* Order Total */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1
            }}>
              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                {language === 'ar' ? keywordsData.total_txt_ar : keywordsData.total_txt_en} :
              </Typography>
              <Typography variant="h5" sx={{ 
                color: '#FFD700', 
                fontWeight: 900,
                fontSize: isMobile ? '1.25rem' : '1.5rem'
              }}>
                {formatPrice(cartTotal.toFixed(2),cartCurrency,language)}
              </Typography>
            </Box>

            {/* Item Count */}
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#6B6B6B',
                textAlign: 'right',
                display: 'block',
                mb: 2,
                fontSize: '0.7rem'
              }}
            >
              {cart.reduce((sum, item) => sum + item.quantity, 0)} {language === 'ar' ? keywordsData.product_txt_ar : keywordsData.product_txt_en}
            </Typography>

            {/* VIP Minimum Charge Notice */}
            {isVipTable && minCharge > 0 && cartTotal < minCharge && (
              <Alert 
                severity="warning" 
                icon={<AttachMoneyIcon />}
                sx={{ 
                  mb: 2,
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  color: '#FF9800',
                  border: '1px solid #FF9800',
                  borderRadius: '10px',
                }}
              >
                <Typography variant="caption">
                  {language === 'ar'
                    ? `الحد الأدنى للطلب ${formatPrice(minCharge,cartCurrency,language)}. الرجاء إضافة منتجات بقيمة ${formatPrice(getRemainingMinCharge().toFixed(2) ,cartCurrency,language)}`
                    : `Minimum order is ${formatPrice(minCharge,cartCurrency,language)}. Please add ${formatPrice(getRemainingMinCharge().toFixed(2)) ,cartCurrency,language} more`
                  }
                </Typography>
              </Alert>
            )}

            {/* Checkout Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={!isCheckoutAllowed()}
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 800,
                fontSize: isMobile ? '0.9rem' : '1rem',
                py: isMobile ? 1.2 : 1.5,
                borderRadius: '50px',
                opacity: isCheckoutAllowed() ? 1 : 0.5,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#FFE55C',
                  transform: isCheckoutAllowed() ? 'translateY(-2px)' : 'none',
                  boxShadow: isCheckoutAllowed() ? '0 6px 20px rgba(255, 215, 0, 0.3)' : 'none',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#2D2D2D',
                  color: '#6B6B6B',
                }
              }}
              onClick={handleCheckout}
            >
              {!isCheckoutAllowed() && isVipTable && minCharge > 0
                ? `${language === 'ar' ? 'أضف' : 'Add'} ${formatPrice(getRemainingMinCharge().toFixed(2),cartCurrency,language)} ${language === 'ar' ? 'إضافية' : 'more'}`
                : totalSavings > 0 ? goCheckoutWithDiscountTxt : goCheckoutTxt
              }
            </Button>

            {/* Continue Shopping Button */}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              sx={{
                mt: 1.5,
                color: '#FFFFFF',
                borderColor: '#2D2D2D',
                borderRadius: '50px',
                py: isMobile ? 1 : 1.2,
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#FFD700',
                  backgroundColor: 'rgba(255, 215, 0, 0.08)',
                  color: '#FFD700',
                  transform: 'translateY(-2px)',
                }
              }}
              onClick={() => setCartOpen(false)}
            >
              {language === 'ar' ? keywordsData.continue_shoping_txt_ar : keywordsData.continue_shoping_txt_en}
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default CartDrawer;